/** API response shapes, shared by the API Worker and the web app. */
import type { IssueAutofixView } from "@monoblock/autofix/src/types";

export interface ProjectInfo {
  id: string;
  name: string;
  timezone: string;
  today: string; // YYYY-MM-DD in the project's timezone
  /** Set on an org-level entry ("org:<id>"), which combines the listed projects. */
  kind?: "org";
  projects?: { id: string; name: string }[];
}

/** The org-level view's split: one row per project and platform ("" when the rollups don't know it). */
export interface ByProject {
  source: DataSource;
  rows: { project_id: string; name: string; platform: string; visitors: number; sessions: number; pageviews: number }[];
}

export interface Totals {
  visitors: number;
  sessions: number;
  pageviews: number;
  bounce_rate: number | null; // 0..1
  avg_duration_ms: number | null;
  conversions: number;
}

export interface DailyRow {
  day: string; // YYYY-MM-DD
  visitors: number;
  sessions: number;
  pageviews: number;
  /** 0..1, null when there were no sessions. */
  bounce_rate: number | null;
  /** Null when unknown (older ranges come from rollups, which don't keep it). */
  avg_duration_ms: number | null;
  conversions: number;
}

export interface BreakdownRow {
  key: string;
  visitors: number;
  sessions: number;
  pageviews: number;
}

export interface GoalRow {
  id: string;
  name: string;
  completions: number;
  visitors: number;
  rate: number | null; // converting visitors / visitors
  value: number;
  /** Same goal over the previous period, for the change pill. */
  prev_rate?: number | null;
  prev_completions?: number;
}

/**
 * hot: Analytics Engine, exact distinct visitors over the range.
 * rollup: nightly D1 rollups, used beyond the hot store's ~90 days; "visitors" is then the
 * sum of daily visitors, which over-counts people who came back on several days.
 */
export type DataSource = "hot" | "rollup";

export interface Overview {
  source: DataSource;
  range: { from: string; to: string; prev_from: string; prev_to: string };
  totals: Totals;
  /**
   * Same-length period immediately before; null when it can't be computed. `visitors` is null
   * when the two periods come from different stores (exact vs summed daily visitors).
   */
  previous: (Omit<Totals, "visitors"> & { visitors: number | null }) | null;
  daily: DailyRow[];
  previous_daily: DailyRow[];
  channels: BreakdownRow[];
  pages: BreakdownRow[];
  goals: GoalRow[];
  live: number | null;
}

export interface Breakdown {
  source: DataSource;
  dimension: string;
  rows: BreakdownRow[];
  /** Distinct visitors across the whole range, for share percentages. */
  total_visitors: number;
}

/** A Sources-table row. Rates are null when the data source can't give them (rollups). */
export interface SourceRow {
  key: string;
  /** Referrer type ("Search", "Social", …) or campaign "medium / source". */
  sub: string;
  visitors: number;
  sessions: number;
  bounce_rate: number | null;
  conv_rate: number | null;
  /** Revenue credited to this source; null when the project has no valued goal (see Sources.currency). */
  revenue: number | null;
  /** Revenue / visitors. */
  per_visitor: number | null;
}

export interface Sources {
  source: DataSource;
  dimension: "channel" | "referrer" | "utm_campaign";
  rows: SourceRow[];
  /** The project's currency when it has a valued goal (so the table shows revenue), else null. */
  currency: string | null;
}

// ---------- revenue ----------

/** Must match REVENUE_DIMENSIONS in @monoblock/query. */
export type RevenueDimension = "channel" | "source" | "referrer" | "utm_source" | "utm_medium" | "utm_campaign" | "entry_page" | "country" | "device";

export interface RevenueTotals {
  revenue: number;
  /** Valued-goal completions (an event matching two valued goals counts once). */
  conversions: number;
  /** Distinct visitors with a valued conversion (summed per day on rollup ranges). */
  converters: number;
}

export interface RevenueRow extends RevenueTotals {
  key: string;
  /** Visitors from this source; null when the traffic breakdown doesn't list it (e.g. "(none)" campaigns). */
  visitors: number | null;
  per_visitor: number | null;
}

export interface RevenueDay {
  day: string; // YYYY-MM-DD
  revenue: number;
  conversions: number;
}

export interface Revenue {
  source: DataSource;
  /** False when the project has no valued goal: the dashboard then shows no revenue at all. */
  enabled: boolean;
  /** ISO 4217 code, one per project (projects.currency, default AED). Amounts are never converted. */
  currency: string;
  /** The valued goals revenue is summed from. */
  goals: { id: string; name: string; value_prop: string }[];
  range: { from: string; to: string; prev_from: string; prev_to: string };
  totals: RevenueTotals & { visitors: number; per_visitor: number | null; per_conversion: number | null };
  /** Same-length period before; null when it can't be read. */
  previous: (RevenueTotals & { per_visitor: number | null }) | null;
  daily: RevenueDay[];
  previous_daily: RevenueDay[];
  dimension: RevenueDimension;
  rows: RevenueRow[];
}

export interface EntryExit {
  source: DataSource;
  entry: { key: string; sessions: number; bounce_rate: number | null }[];
  exit: { key: string; exits: number; exit_rate: number | null }[];
}

export type VitalKey = "lcp" | "inp" | "cls" | "fcp" | "ttfb";
/** p75 of each metric (CLS unitless, others ms) and how many page loads reported it. */
export type VitalSet = Partial<Record<VitalKey, { p75: number; samples: number; good?: number; ni?: number; poor?: number }>>;

export interface Vitals {
  dimension: string;
  /** True when built from daily rollups (p75 is a weighted average of daily p75s). */
  approximate?: boolean;
  overall: VitalSet;
  rows: ({ key: string } & VitalSet)[];
}

export interface GoalDef {
  id: string;
  name: string;
  kind: "event" | "pageview" | "click";
  match: string;
  /** Event property holding the goal's amount (e.g. "value"); null for goals without one. */
  value_prop: string | null;
}

export interface FunnelReport {
  funnel: { id: string; name: string; window_days: number; steps: { kind: string; match: string; label: string }[] };
  steps: { label: string; visitors: number; conversion: number | null; dropoff: number | null; median_ms: number | null }[];
  /** True when a step hit the row limit, so counts are a lower bound. */
  sampled: boolean;
}

export interface JourneyRow {
  key: string;
  steps: number;
  sessions: number;
}

export type Journey =
  | { page: null; ignored_filters: string[]; flows: { from: string; to: string; steps: number; sessions: number }[] }
  | {
      page: string;
      ignored_filters: string[];
      arrivals: number;
      sessions: number;
      /** Visits that started on this page. */
      entries: number;
      /** Visits that ended on this page. */
      exits: number;
      previous: JourneyRow[];
      next: JourneyRow[];
      entry_sources: JourneyRow[];
    };

export interface VisitSummary {
  sid: string;
  visitor: string;
  started: number;
  ended: number;
  views: number;
  events: number;
  channel: string;
  source: string;
  country: string;
  device: string;
  browser: string;
  platform: string;
  user_id: string;
}

export interface VisitEvent {
  type: string;
  name: string;
  path: string;
  ts: number;
  props: string;
}

// ---------- errors ----------

export type ErrorStatus = "open" | "resolved" | "ignored";

export interface ErrorGroup {
  fingerprint: string;
  type: string;
  message: string;
  first_seen: number; // ms
  last_seen: number; // ms
  count: number;
  users: number;
  status: ErrorStatus;
  last_release: string | null;
  /** 1 when every frame is a third-party script (Shopify's hosted code): grouped as ignored. */
  third_party?: number;
}

/** A stack frame, with its original position when a source map for the release was uploaded. */
export interface ErrorFrame {
  fn: string;
  file: string;
  line: number | null;
  col: number | null;
  in_app: boolean;
  original?: { source: string; line: number; col: number; name: string | null; context: string | null };
}

export interface ErrorOccurrence {
  event_id: string;
  ts: number;
  message: string;
  release: string | null;
  url: string | null;
  browser: string | null;
  os: string | null;
  device: string | null;
  country: string | null;
}

export interface ErrorIssue {
  group: ErrorGroup & { sample_url: string | null };
  /** The latest occurrence's stack, parsed and symbolicated where possible. */
  frames: ErrorFrame[];
  raw_stack: string | null;
  /** Release the stack was symbolicated against; `symbolicated` says whether any map matched. */
  release: string | null;
  symbolicated: boolean;
  occurrences: ErrorOccurrence[];
  /** Auto-fix for this group (signed-in views only; not on share links). */
  autofix?: IssueAutofixView;
}

/** Colour family of a chart note. */
export const NOTE_CATEGORIES = ["note", "launch", "campaign", "incident"] as const;
export type NoteCategory = (typeof NOTE_CATEGORIES)[number];

/** A chart note (annotation): one short line pinned to a day of a project's charts. */
export interface ChartNote {
  id: string;
  project_id: string;
  day: string; // YYYY-MM-DD, the project's calendar day
  text: string; // up to 140 characters
  category: NoteCategory;
  /** Who added it; left empty on share links, which don't show staff emails. */
  created_by: string;
  created_at: number;
  updated_at: number;
}
